import { COLLISION_CELLS_PER_TILE, type LayerDocument, type MapDocument } from "../document/types.ts";
import { baseLayerId } from "./base-layer.ts";

/**
 * The area (ADR-128 sections A and B): one rectangle of whole tiles on one map and the layers it takes. With the
 * `all` reach that is every visible, unlocked layer of every kind, never the base unless the base is unlocked and
 * the builder ticked "Include the base"; with the `layer` reach the active layer alone. The rectangle is read in
 * each grid's own stride: a tile layer's cells, a terrain layer's corners (one more column and row), and the
 * quarter cells of a collision, zone or door layer (`COLLISION_CELLS_PER_TILE` each way a tile). Everything here
 * is plain data; the store holds the view state it is read from.
 */

/**
 * A rectangle of whole tiles, both corners inclusive, as `tileSelection` holds it.
 */
export interface AreaRect {
  readonly x0: number;
  readonly y0: number;
  readonly x1: number;
  readonly y1: number;
}

/**
 * A move's offset in whole tiles: `dx` to the right and `dy` down, so "26 right and 28 up" is `{ dx: 26, dy: -28 }`.
 */
export interface AreaOffset {
  readonly dx: number;
  readonly dy: number;
}

/**
 * The two reaches (decision 1): the active layer alone, or every visible, unlocked layer.
 */
export type AreaReachKind = "layer" | "all";

/**
 * What the reach is read from, all view state: the reach, the active layer, the layers locked and hidden (by their
 * own lock or eye or their group's), and whether "Include the base" is ticked (decision 3d).
 */
export interface AreaView {
  readonly reach: AreaReachKind;
  readonly activeLayerId: string | null;
  readonly locked: readonly string[];
  readonly hidden: readonly string[];
  readonly includeBase: boolean;
}

/**
 * Why a layer is left out of the `all` reach (decision 5): it is the base, it is locked, or it is hidden.
 */
export type AreaLeftReason = "base" | "locked" | "hidden";

/**
 * A layer the reach leaves out, and why.
 */
export interface AreaLeftLayer {
  readonly layerId: string;
  readonly why: AreaLeftReason;
}

/**
 * The reach read against a map: the layers taken and the layers left out with their reason, each in the map's
 * order. With the `layer` reach only the active layer is ever named.
 */
export interface AreaReach {
  readonly taken: readonly string[];
  readonly left: readonly AreaLeftLayer[];
}

/**
 * The three grids a layer can hold: tile cells, terrain corners and quarter cells.
 */
export type AreaGrid = "tiles" | "corners" | "quarters";

/**
 * Why a layer is left out by its lock or its eye, locked before hidden as Replace in selection reads it.
 *
 * @param layerId The layer.
 * @param locked The layers locked.
 * @param hidden The layers hidden.
 * @returns The reason, or null when the layer may be edited.
 */
function blocked(layerId: string, locked: ReadonlySet<string>, hidden: ReadonlySet<string>): "locked" | "hidden" | null {
  return locked.has(layerId) ? "locked" : hidden.has(layerId) ? "hidden" : null;
}

/**
 * The layers an area takes on a map, and the layers it leaves with their reason (decisions 1, 3a, 3d, 4 and 42).
 * With the `all` reach the base is left as "base" unless it is unlocked and "Include the base" is ticked, in which
 * case only its eye can leave it; every other layer is left when locked or hidden. With the `layer` reach the
 * active layer is taken when its lock and eye allow, the base among them once a builder has unlocked it.
 *
 * @param map The map in view.
 * @param view The view state the reach is read from.
 * @returns The layers taken and left, in the map's order.
 */
export function areaReach(map: MapDocument, view: AreaView): AreaReach {
  const locked = new Set(view.locked);
  const hidden = new Set(view.hidden);
  if (view.reach === "layer") {
    const active = map.layers.find((layer) => layer.id === view.activeLayerId);
    if (active === undefined) {
      return { taken: [], left: [] };
    }
    const why = blocked(active.id, locked, hidden);
    return why === null ? { taken: [active.id], left: [] } : { taken: [], left: [{ layerId: active.id, why }] };
  }
  const base = baseLayerId(map);
  const taken: string[] = [];
  const left: AreaLeftLayer[] = [];
  for (const layer of map.layers) {
    const why = layer.id === base && (locked.has(base) || !view.includeBase) ? "base" : blocked(layer.id, locked, hidden);
    if (why === null) {
      taken.push(layer.id);
    } else {
      left.push({ layerId: layer.id, why });
    }
  }
  return { taken, left };
}

/**
 * Checks that a rectangle is one an area can hold: whole tiles, corners in order, inside the map.
 *
 * @param map The map.
 * @param rect The rectangle.
 * @throws RangeError when a corner is not a whole number, the corners are out of order, or a corner lies outside
 *   the map.
 */
export function assertAreaRect(map: MapDocument, rect: AreaRect): void {
  const whole = [rect.x0, rect.y0, rect.x1, rect.y1].every((n) => Number.isInteger(n));
  if (!whole || rect.x0 > rect.x1 || rect.y0 > rect.y1 || rect.x0 < 0 || rect.y0 < 0 || rect.x1 >= map.width || rect.y1 >= map.height) {
    throw new RangeError(`area ${rect.x0},${rect.y0} to ${rect.x1},${rect.y1} is not a rectangle of whole tiles inside the ${map.width} by ${map.height} map`);
  }
}

/**
 * The grid a layer holds, or null for a layer of props or markers.
 *
 * @param layer The layer.
 * @returns Its grid.
 */
export function areaGridOf(layer: LayerDocument): AreaGrid | null {
  switch (layer.kind) {
    case "tile":
      return "tiles";
    case "terrain":
      return "corners";
    case "collision":
    case "zone":
    case "door":
      return "quarters";
    default:
      return null;
  }
}

/**
 * How many grid columns a tile spans in a grid: one for tiles and corners, `COLLISION_CELLS_PER_TILE` for
 * quarter cells.
 *
 * @param grid The grid.
 * @returns The scale.
 */
export function areaGridScale(grid: AreaGrid): number {
  return grid === "quarters" ? COLLISION_CELLS_PER_TILE : 1;
}

/**
 * The width and height of a map's grid.
 *
 * @param map The map.
 * @param grid The grid.
 * @returns Its columns and rows.
 */
export function areaGridSize(map: MapDocument, grid: AreaGrid): { readonly width: number; readonly height: number } {
  const extra = grid === "corners" ? 1 : 0;
  const scale = areaGridScale(grid);
  return { width: map.width * scale + extra, height: map.height * scale + extra };
}

/**
 * The rectangle of tiles read in a grid, both corners inclusive (decision 9): the same cells for tiles, columns
 * `x0` to `x1 + 1` and rows `y0` to `y1 + 1` for corners, and `4 * x0` to `4 * (x1 + 1) - 1` for quarter cells.
 *
 * @param rect The rectangle of tiles.
 * @param grid The grid.
 * @returns The rectangle in the grid's columns and rows.
 */
export function areaGridBounds(rect: AreaRect, grid: AreaGrid): AreaRect {
  if (grid === "corners") {
    return { x0: rect.x0, y0: rect.y0, x1: rect.x1 + 1, y1: rect.y1 + 1 };
  }
  const scale = areaGridScale(grid);
  return { x0: rect.x0 * scale, y0: rect.y0 * scale, x1: (rect.x1 + 1) * scale - 1, y1: (rect.y1 + 1) * scale - 1 };
}

/**
 * Whether a prop or a marker stands in a rectangle: its anchor cell, `floor(x)` and `floor(y)`, the cell its art
 * stands on, lies inside (decisions 9 and 10).
 *
 * @param rect The rectangle of tiles.
 * @param x The thing's column, in tiles.
 * @param y The thing's row, in tiles.
 * @returns True when the anchor cell is inside.
 */
export function anchoredIn(rect: AreaRect, x: number, y: number): boolean {
  const column = Math.floor(x);
  const row = Math.floor(y);
  return column >= rect.x0 && column <= rect.x1 && row >= rect.y0 && row <= rect.y1;
}

/**
 * A rectangle moved by an offset.
 *
 * @param rect The rectangle.
 * @param offset The offset, in the rectangle's units.
 * @returns The moved rectangle.
 */
export function shiftedRect(rect: AreaRect, offset: AreaOffset): AreaRect {
  return { x0: rect.x0 + offset.dx, y0: rect.y0 + offset.dy, x1: rect.x1 + offset.dx, y1: rect.y1 + offset.dy };
}
